import { Color, space, colors, solid2 } from '@ardoq/design-tokens';
import { FlexBox } from '@ardoq/layout';
import { caption, text2Bold } from '@ardoq/typography';
import { KeyboardEventHandler, MouseEventHandler } from 'react';
import styled from 'styled-components';
import { StrikethroughLocation } from './types';

export const StepContainer = styled(FlexBox).attrs({
  gap: 'medium',
})<{
  $hasBorder: boolean;
  $borderColor: Color;
  $isDisabled?: boolean;
  $isSelected?: boolean;
  $isInteractive: boolean;
  $rounded?: boolean;
  onClick?: MouseEventHandler<HTMLDivElement>;
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
  tabIndex?: number;
}>`
  flex: 1;
  position: relative;
  isolation: isolate;
  border-top: ${({ $hasBorder }) => ($hasBorder ? solid2 : 'none')};
  border-color: ${({ $borderColor: borderColor }) => colors[borderColor]};
  padding: ${space.small} ${space.small} ${space.small} ${space.medium};

  ${({ $isInteractive }) =>
    $isInteractive &&
    `
    cursor: pointer;
    &:hover {
      background-color: ${colors.grey50};
    }
    &:active {
      background-color: ${colors.grey300};
    }
  `}

  ${({ $isDisabled }) =>
    $isDisabled &&
    `
    pointer-events: none;
    color: ${colors.textDisabled};
  `}

  ${({ $isSelected }) => $isSelected && `background-color: ${colors.grey200}`};
  ${({ $rounded }) => $rounded && `border-radius: ${space.xsmall}`};
`;

export const SubStepContainer = styled(StepContainer)`
  padding: ${space.small} ${space.small} ${space.small} 20px;
`;

export const StepIconWrapper = styled(FlexBox).attrs({
  align: 'center',
  justify: 'center',
  gap: 'medium',
})<{ $color: Color; $isFilled: boolean }>`
  ${text2Bold};

  width: ${space.large};
  height: ${space.large};
  border-radius: 50%;
  border: ${solid2};
  border-color: ${({ $color }) => colors[$color]};
  background-color: ${({ $isFilled, $color }) =>
    $isFilled ? colors[$color] : 'white'};
  color: ${({ $isFilled, $color }) =>
    $isFilled ? colors.white950 : colors[$color]};
  z-index: 2;
`;

export const SubStepIconWrapper = styled(StepIconWrapper)`
  width: ${space.medium};
  height: ${space.medium};
  padding: 2px;
  > div {
    background-color: ${({ $color }) => colors[$color]};
  }
`;

export const StrikeThroughLine = styled.div<{
  $location: StrikethroughLocation;
  $color: Color;
}>`
  position: absolute;
  left: 27px;
  width: 2px;
  z-index: 1;
  height: ${({ $location }) => ($location === 'both' ? '100%' : '50%')};
  ${({ $location }) =>
    ($location === 'top' || $location === 'both') && 'top: 0'};
  ${({ $location }) => $location === 'bottom' && 'bottom: 0'};
  background-color: ${({ $color }) => colors[$color]};
`;

export const Heading = styled.div`
  ${text2Bold};
  /* Design choice from the design system.
   We want the icon to be centered with the Heading text but not with subheading text. */
  line-height: ${space.large};
`;

export const SubHeading = styled.div<{ $isDisabled?: boolean }>`
  ${caption};
  color: ${({ $isDisabled }) =>
    $isDisabled ? colors.textDisabled : colors.textSubtle};
`;
